import React, {RefObject} from "react";
import {withTranslation, WithTranslation} from "react-i18next";
import {
    KatLabel,
    KatInputGroup,
    KatPopover,
    KatDropdown,
    KatOption,
    KatIcon,
    KatCheckbox
} from "@amzn/katal-react";
import MetricsUtil from "src/utils/MetricsUtil";
import {
    getCurrencySymbolConfig,
    getProgramInventoryCountryListConfig, getProgramStoreCountryListConfig, programWithCeeParam,
    programWithInventoryAndStoreParam
} from "src/config/Config";
import CurrencyItem from "src/model/CurrencyItem";
import NumberKatInput from "../common/NumberKatInput";
import QueryProductInfo from "src/model/QueryProduct";

import {bindActionCreators, Dispatch} from "redux";
import {connect} from "react-redux";
import {setUpdateProgramFeesQueryStart} from "src/actions/ProgramFeesQueryAction";
import {setUpdateNewProgramFeesQueryStart} from "src/actions/NewProgramFeesQueryAction";
import {RecommendationInfo} from "src/model/Recommendation";
import PriceRecommendationComponent from "src/components/recommendation/PriceRecommendationComponent";
import {ProgramParam} from "src/model/ProgramFees";
import ApplicationState from "src/model/ApplicationState";

interface StateProps {
}

export interface ProgramProductCostProps {
    selectedProgram: string;
    isMFN: boolean;
    countryCode: string;
    queryProduct: QueryProductInfo;
    defaultSalesPrice: CurrencyItem;
    defaultShippingCharge: CurrencyItem;
    recommendationInfo?: RecommendationInfo;
    handleChangeSalesPrice: Function;
    handleChangeShippingCharge: Function;
}

interface OwnProps {
    readonly metricsUtil?: MetricsUtil;
    readonly nextRef?: RefObject<any>;
}

const mapStateToProps = (state: ApplicationState): StateProps => ({});

interface DispatchProps {
    readonly dispatchUpdateProgramFees: (countryCode: string, queryProduct: QueryProductInfo, programIdList: Array<string>, programParamMap?: Record<string, ProgramParam>) => void,
    readonly dispatchUpdateNewProgramFees: (countryCode: string, queryProduct: QueryProductInfo, programIdList: Array<string>, programParamMap?: Record<string, ProgramParam>) => void,
}

const mapDispatchToProps = (dispatch: Dispatch) => (
    bindActionCreators({
        dispatchUpdateProgramFees: setUpdateProgramFeesQueryStart,
        dispatchUpdateNewProgramFees: setUpdateNewProgramFeesQueryStart
    }, dispatch)
);

const connector = connect(mapStateToProps, mapDispatchToProps);

type Props = StateProps & ProgramProductCostProps & OwnProps & DispatchProps & WithTranslation;

interface State {
    salesPrice: CurrencyItem;
    shippingCharge: CurrencyItem;
    cee: boolean;
    inventoryPlacedIn: string;
    amazonStore: string;
}

export class ProgramRevenueComponent extends React.Component<Props, State> {
    private readonly metricsUtil: MetricsUtil;
    private readonly shippingChargeInputRef: RefObject<any>;

    constructor(props: Props) {
        super(props);

        this.state = {
            salesPrice: this.props.defaultSalesPrice, // input
            shippingCharge: this.props.defaultShippingCharge, // input
            cee: true,
            amazonStore: this.props.countryCode,
            inventoryPlacedIn: getProgramInventoryCountryListConfig(props.selectedProgram)[0]
        };

        if (this.props.metricsUtil) {
            this.metricsUtil = this.props.metricsUtil;
        } else {
            this.metricsUtil = MetricsUtil.getDefaultMetricUtil();
        }

        // Used for input box focus switch by Enter key, link input box cross inner components
        this.shippingChargeInputRef = React.createRef<any>();

        this.handleChangeInventory = this.handleChangeInventory.bind(this);
        this.handleChangeStore = this.handleChangeStore.bind(this);
        this.handleChangeCee = this.handleChangeCee.bind(this);
        this.updateProgramParam = this.updateProgramParam.bind(this);
        this.needShowRecommendation = this.needShowRecommendation.bind(this);
    }

    // After commit the change, dispatch an action to call backend to update fees data.
    handleSalesPriceChange(input: number) {
        const currencyUnit = getCurrencySymbolConfig(this.state.amazonStore);
        this.setState({salesPrice: {amount: input, currency: currencyUnit}});
        this.props.handleChangeSalesPrice({amount: input, currency: currencyUnit})
        const queryProductNew = {
            ...this.props.queryProduct,
            afnPriceStr: input + "",
            mfnPriceStr: input + "",
            mfnShippingPriceStr: this.state.shippingCharge.amount + "",
        }
        this.updateProgramParam(queryProductNew);
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("salesPrice", "input");
        }
    }

    // After commit the change, dispatch an action to call backend to update fees data.
    handleShippingChargeChange(input: number) {
        const currencyUnit = getCurrencySymbolConfig(this.state.amazonStore);
        this.setState({shippingCharge: {amount: input, currency: currencyUnit}});
        this.props.handleChangeShippingCharge({amount: input, currency: currencyUnit});
        const queryProductNew = {
            ...this.props.queryProduct,
            afnPriceStr: this.state.salesPrice.amount + "",
            mfnPriceStr: this.state.salesPrice.amount + "",
            mfnShippingPriceStr: input + "",
        }
        this.updateProgramParam(queryProductNew);
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("shippingCharge", "input");
        }
    }

    handleChangeInventory(event: KatDropdown.ChangeEvent) {
        this.setState({inventoryPlacedIn: event.detail.value});
        this.updateProgramParam(this.props.queryProduct);
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("programParam_inventoryPlacedIn", "select");
        }
    }

    handleChangeStore(event: KatDropdown.ChangeEvent) {
        this.setState({amazonStore: event.detail.value});
        this.updateProgramParam(this.props.queryProduct);
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("programParam_amazonStore", "select");
        }
    }

    handleChangeCee(event: KatCheckbox.ChangeEvent) {
        this.setState({cee: event.detail.checked});
        this.updateProgramParam(this.props.queryProduct);
        if (this.metricsUtil) {
            this.metricsUtil.publishCounter("programParam_cee", "select");
        }

    }

    updateProgramParam(product: QueryProductInfo) {
        let param;
        let programParamMap;
        if (this.isProgramWithCeeParam(this.props.selectedProgram)) {
            param = {
                cee: this.state.cee
            }
        }
        if (this.isProgramWithInventoryStoreParam(this.props.selectedProgram)) {
            param = {
                ...param,
                marketplace: this.state.amazonStore,
                inventoryPlace: this.state.inventoryPlacedIn,
            }
            programParamMap = {[this.props.selectedProgram]: param};
        }
        if (this.props.queryProduct.isNewDefined) {
            this.props.dispatchUpdateNewProgramFees(this.props.countryCode, product, [this.props.selectedProgram], programParamMap);
        } else {
            this.props.dispatchUpdateProgramFees(this.props.countryCode, product, [this.props.selectedProgram], programParamMap);
        }
    }

    generateLabelWithOptionalPopover(labelStringId: string, popoverStringId?: string) {
        const {t} = this.props;
        if (popoverStringId) {
            return (
                <KatPopover triggerType="hover" katAriaBehavior="tooltip" position="bottom-start">
                    <KatLabel slot="trigger" text={t(labelStringId)} className="input-block-text"/>
                    <KatLabel text={t(popoverStringId)} className="popover-content"/>
                </KatPopover>
            );
        } else {
            return (
                <KatLabel text={t(labelStringId)} className="input-block-text"/>
            );
        }
    }

    getLabelDropdown(labelId: string, dropdownValue: string, dropdownList: Array<string>, dropdownFn: Function) {
        const {t} = this.props;
        return (
            <>
                <div className="section-content param-content">
                    <div className="input-block input-block-padding">
                        <KatLabel text={t(labelId)} className={"input-block-text"}/>
                        <div className="input-block-group-box">
                            <KatDropdown rich-selection-label value={dropdownValue} size="small"
                                //@ts-ignore
                                         className="country-dropdown" onChange={dropdownFn}>
                                {
                                    dropdownList.map((country, index) => (
                                        <KatOption value={country} key={index}>
                                            <div className="flexed">
                                                <KatIcon name={("flag-" + country.toLowerCase()) as KatIcon.Name} size="small"/>
                                                <span className="paddedMedium">{country}</span>
                                            </div>
                                        </KatOption>
                                    ))
                                }
                            </KatDropdown>
                        </div>
                    </div>
                </div>
            </>)
    }

    isProgramWithInventoryStoreParam(selectedProgram: string) {
        return programWithInventoryAndStoreParam.indexOf(selectedProgram) >= 0;
    }

    isProgramWithCeeParam(selectedProgram: string) {
        return programWithCeeParam.indexOf(selectedProgram) >= 0;
    }


    getInventoryStoreDropdown(selectedProgram: string) {
        const {t} = this.props;
        return (
            <>
                {this.isProgramWithInventoryStoreParam(selectedProgram) &&
                <>
                    {this.getLabelDropdown("SC_FBA_Calc_label_inventoryPlacedIn", this.state.inventoryPlacedIn, getProgramInventoryCountryListConfig(selectedProgram), this.handleChangeInventory)}
                    {this.getLabelDropdown("SC_FBA_Calc_label_country", this.state.amazonStore, getProgramStoreCountryListConfig(selectedProgram), this.handleChangeStore)}
                </>
                }
                {
                    // Now Core program is base on seller current status. So we only show it in other program.
                    // CEE means seller only have inventory in DE.
                    this.state.inventoryPlacedIn == "DE" && this.isProgramWithCeeParam(selectedProgram) && <>
                        <div className="section-content param-content">
                            <div className="service-addon-box-content">
                                <div className="checkbox-with-label">
                                    <KatCheckbox className="input-block-text" label=" " checked={this.state.cee}
                                                 onChange={this.handleChangeCee}/>
                                    <KatPopover triggerType="hover" katAriaBehavior="tooltip" position="bottom-start">
                                        <KatLabel slot="trigger" text={t("SC_FBA_Calc_label_cee")} className="input-block-text"/>
                                        <KatLabel text={t("SC_FBA_Calc_tip_cee")} className="popover-content"/>
                                    </KatPopover>
                                </div>
                            </div>
                        </div>
                    </>
                }
            </>
        )
    }

    private needShowRecommendation(): boolean {
        const {recommendationInfo} = this.props;
        if (recommendationInfo != undefined && recommendationInfo.attributes['CURRENCY_CODE'] == this.state.salesPrice.currency
            && recommendationInfo.attributes['YOUR_CURRENT_PRICE'] == this.state.salesPrice.amount + '') {
            // Only display the recommendation when current price match
            return true;
        }
        return false;
    }

    render() {
        const {recommendationInfo} = this.props;
        const currencyUnit = getCurrencySymbolConfig(this.state.amazonStore);
        const showRecommendation = this.needShowRecommendation();

        const salesPriceContent = (
            <div className={showRecommendation ? "input-block-with-recommendation" : "input-block input-block-padding"}>
                {this.generateLabelWithOptionalPopover("SC_FBA_Calc_label_salesPrice", "SC_FBA_Calc_tt_itemPrice_new")}
                <div className="input-block-group-box">
                    <KatInputGroup className="input-block-inputgroup">
                        <KatLabel text={currencyUnit}/>
                        <NumberKatInput isDisabled={false} className="input-price" minValue={0} isFloat={true} isMandatory={true}
                                        nextRef={this.props.isMFN ? this.shippingChargeInputRef : this.props.nextRef}
                                        value={this.state.salesPrice.amount}
                                        onChange={(input: number, hasError: boolean): void => {
                                            if (!hasError) {
                                                this.handleSalesPriceChange(input);
                                            }
                                        }}/>
                    </KatInputGroup>
                    {recommendationInfo && showRecommendation &&
                    <PriceRecommendationComponent recommendationInfo={recommendationInfo} metricsUtil={this.metricsUtil}/>}
                </div>
            </div>
        );

        const shippingChargeContent = (
            <div className="input-block input-block-padding">
                {this.generateLabelWithOptionalPopover("SC_FBA_Calc_label_shippingCharge", "Olympus_Migration_SC_FBA_Calc_tt_shipping_new")}
                <div className="input-block-group-box">
                    <KatInputGroup className="input-block-inputgroup">
                        <KatLabel text={currencyUnit}/>
                        <NumberKatInput isDisabled={false} className="input-price" minValue={0} isFloat={true}
                                        ref={this.shippingChargeInputRef} nextRef={this.props.nextRef}
                                        value={this.state.shippingCharge.amount}
                                        onChange={(input: number, hasError: boolean): void => {
                                            if (!hasError) {
                                                this.handleShippingChargeChange(input);
                                            }
                                        }}/>
                    </KatInputGroup>
                </div>
            </div>
        );

        return (
            <div className="revenue-section">
                {this.getInventoryStoreDropdown(this.props.selectedProgram)}
                <div className="section-content">
                    {salesPriceContent}
                    {this.props.isMFN ? shippingChargeContent : <></>}
                </div>
            </div>
        );
    }
}

export default withTranslation()(connector(ProgramRevenueComponent));
